<template>
  <!-- data-holiday-id — toggleRow's scroll anchoring looks the row up by it. -->
  <div class="holiday-row" :class="{ configured: isConfigured, open, flagged: isFlagged }" :data-holiday-id="holiday.id">
    <div class="row-main">
      <div
        class="row-head"
        role="button"
        tabindex="0"
        :aria-expanded="open ? 'true' : 'false'"
        :title="open ? 'Click to collapse' : 'Click to edit'"
        @click="onRowHeadClick"
        @keydown.enter.prevent="onRowHeadClick"
        @keydown.space.prevent="onRowHeadClick"
      >
        <h4 class="row-name">{{ holiday.name }}</h4>
        <span class="row-summary" :class="statusClass">
          <span v-if="isFlagged" class="flag-prefix" aria-hidden="true">⚑ </span>{{ summary }}
        </span>
        <span
          v-if="hasNote"
          class="note-indicator"
          title="Has rationale note"
          aria-label="Has rationale note"
        >💬</span>
        <button
          type="button"
          class="row-flag"
          :class="{ active: isFlagged }"
          :title="isFlagged ? 'Remove discuss flag — mark as in agreement' : 'Mark for discussion'"
          :aria-label="isFlagged ? 'Remove discuss flag' : 'Mark for discussion'"
          :aria-pressed="isFlagged ? 'true' : 'false'"
          :disabled="role === 'viewer'"
          @click.stop="toggleFlag"
        >⚑</button>
        <span class="row-chevron" aria-hidden="true">▾</span>
        <button
          v-if="canEdit"
          type="button"
          class="row-remove"
          :title="`Remove ${holiday.name}`"
          :aria-label="`Remove ${holiday.name}`"
          @click.stop="$emit('remove', holiday.id)"
        >×</button>
      </div>

      <div v-if="open" class="row-body">
        <!-- Discussion note panel. Writable for the secondary role (comment-only user);
             read-only for the primary role, with a "Mark resolved" button that clears
             both the note and the flag together. Rendered above the rule editor so
             the primary sees the comment before the controls. -->
        <div v-if="canComment || hasNote" class="discussion-note-panel">
          <div class="discussion-note-head">
            <span class="discussion-note-label">
              <span aria-hidden="true">💬</span>
              {{ noteLabel }}
            </span>
            <button
              v-if="canEdit && (hasNote || isFlagged)"
              type="button"
              class="resolve-btn"
              title="Clear the flag and note"
              @click="resolveDiscussion"
            >Mark resolved</button>
          </div>
          <textarea
            v-if="canComment"
            class="discussion-note-textarea"
            :value="noteText"
            rows="3"
            placeholder="What would you like to discuss about this item? Only the other party will see this."
            @input="onNoteInput($event.target.value)"
          ></textarea>
          <template v-else>
            <p class="discussion-note-readonly">{{ noteText }}</p>
            <p v-if="noteMeta" class="discussion-note-meta">{{ noteMeta }}</p>
          </template>
        </div>

        <RuleEditor
          v-if="canEdit"
          :value="holiday.rule"
          :partyAName="partyAName"
          :partyBName="partyBName"
          :longWeekendType="holiday.longWeekendType"
          :category="holiday.category"
          :holidayId="holiday.id"
          @input="onRuleChange"
        />
        <button v-if="canEdit" type="button" class="details-toggle" @click="detailsOpen = !detailsOpen">
          {{ detailsOpen ? '− Hide details' : '+ Pickup, drop-off, notes' }}
        </button>
        <div v-if="canEdit && detailsOpen" class="details-grid">
          <label class="span-2">
            <span>Display name</span>
            <input
              type="text"
              :value="holiday.name"
              maxlength="80"
              placeholder="Holiday name shown throughout the app and in the PDF"
              @input="emitPatch({ name: $event.target.value, customName: true })"
            />
          </label>
          <!-- Dates: computed holidays (stats, Easter chain) resolve on their
               own and show no editor. School breaks change with every school
               calendar, so they get PER-YEAR dates below. Birthdays / custom
               days get one date that repeats every year. -->
          <div v-if="isRange" class="span-2 break-years">
            <span class="break-years-title">Dates by year — school calendars change, so set each year's actual dates</span>
            <div v-for="y in breakYears" :key="y" class="break-year-row">
              <span class="break-year-label">{{ y }}</span>
              <DateField
                :value="yearRange(y).start"
                @input="patchYearDates(y, $event, yearRange(y).end)"
              />
              <span class="break-year-to">to</span>
              <DateField
                :value="yearRange(y).end"
                @input="patchYearDates(y, yearRange(y).start, $event)"
              />
              <span v-if="!yearRange(y).start" class="break-year-default">using {{ yearDefaultLabel(y) }}</span>
              <button
                v-else
                type="button"
                class="break-year-clear"
                title="Back to the default dates for this year"
                @click="patchYearDates(y, '', '')"
              >×</button>
            </div>
          </div>

          <template v-else-if="datesEditable">
            <label>
              <span>Date (repeats yearly)</span>
              <DateField
                :value="dateRangeStart"
                @input="patchDates($event, dateRangeEnd)"
              />
            </label>
            <label>
              <span>Ends (optional)</span>
              <DateField
                :value="dateRangeEnd"
                @input="patchDates(dateRangeStart, $event)"
              />
            </label>
            <p v-if="!holiday.dateRange" class="span-2 dates-hint">
              Set a date so this appears on the calendar views.
            </p>
            <button
              v-else
              type="button"
              class="span-2 dates-reset"
              @click="emitPatch({ dateRange: null })"
            >↺ Clear dates</button>
          </template>

          <label class="span-2 times-agreed-row">
            <input
              type="checkbox"
              :checked="holiday.timesByAgreement"
              @change="emitPatch({ timesByAgreement: $event.target.checked })"
            />
            <span>Times to be agreed by the parties</span>
          </label>
          <label :class="{ disabled: holiday.timesByAgreement }">
            <span>Pickup time</span>
            <input
              type="time"
              :value="holiday.pickup"
              :disabled="holiday.timesByAgreement"
              @input="emitPatch({ pickup: $event.target.value })"
            />
            <small v-if="holiday.pickup && !holiday.timesByAgreement" class="time-hint">{{ formatTimeOfDay(holiday.pickup) }}</small>
          </label>
          <label :class="{ disabled: holiday.timesByAgreement }">
            <span>Drop-off time</span>
            <input
              type="time"
              :value="holiday.dropoff"
              :disabled="holiday.timesByAgreement"
              @input="emitPatch({ dropoff: $event.target.value })"
            />
            <small v-if="holiday.dropoff && !holiday.timesByAgreement" class="time-hint">{{ formatTimeOfDay(holiday.dropoff) }}</small>
          </label>
          <label class="span-2">
            <span>Notes</span>
            <input type="text" :value="holiday.notes" placeholder="e.g. pickup at the school" @input="emitPatch({ notes: $event.target.value })" />
          </label>
          <label class="span-2">
            <span>Why this matters (private note for the other parent)</span>
            <textarea
              :value="holiday.whyThisMatters"
              rows="2"
              placeholder="e.g. My mom always hosts Christmas Eve, so it's important to me that the kids are there for it."
              @input="emitPatch({ whyThisMatters: $event.target.value })"
            ></textarea>
          </label>
          <label class="span-2">
            <span>Custom wording (replaces the part after "For {{ holiday.name }},")</span>
            <textarea
              :value="holiday.customProse"
              rows="2"
              :placeholder="`e.g. the children will be with both parents at the school holiday concert.`"
              @input="emitPatch({ customProse: $event.target.value })"
            ></textarea>
          </label>
        </div>
        <button
          v-if="canEdit && isConfigured"
          type="button"
          class="apply-to-all"
          title="Copy this arrangement onto every other row in this section"
          @click="$emit('apply-to-category', holiday.id)"
        >
          ↓ Use this arrangement for every row in this section
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import RuleEditor from './RuleEditor.vue'
import DateField from '../../DateField.vue'
import { summarizeRule, formatTimeOfDay } from '@/lib/parentingSchedule/proseRenderer'
import { hasDateRule, resolveHolidayDate } from '@/lib/parentingSchedule/dates'

export default {
  name: 'HolidayRow',
  components: { RuleEditor, DateField },
  props: {
    holiday: { type: Object, required: true },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    jurisdiction: { type: String, default: 'BC' },
    open: { type: Boolean, default: false },
    // Permission role for this row. 'primary' can edit rules, times, and
    // custom membership; 'secondary' can only toggle the discuss flag and
    // write the discussionNote. Both roles can toggle the flag — that's how
    // the secondary user raises an item for discussion.
    role: {
      type: String,
      default: 'primary',
      validator: function (v) { return v === 'primary' || v === 'secondary' || v === 'viewer' },
    },
    // Stamped onto every note this session writes so the receiving party
    // sees "Note from Gwyneth" instead of "Note from the other party".
    currentUserName: { type: String, default: '' },
  },
  data() {
    // detailsOpen tracks whether the secondary pickup/notes panel is showing.
    // It's nested *inside* the row body, so it only matters when `open` is true.
    return { detailsOpen: false }
  },
  computed: {
    // Stat holidays and the Easter chain compute their own dates — no editor.
    // School breaks have provincial defaults worth overriding; birthdays,
    // pro-d days and custom holidays have no dates until set here.
    hasBuiltInDates() {
      return hasDateRule(this.holiday.id)
    },
    datesEditable() {
      return !this.hasBuiltInDates || this.holiday.category === 'school-break'
    },
    isRange() {
      return this.holiday.category === 'school-break'
    },
    breakYears() {
      const y = new Date().getFullYear()
      return [y, y + 1, y + 2]
    },
    dateRangeStart() {
      return this._toInputDate(this.holiday.dateRange?.start)
    },
    dateRangeEnd() {
      return this._toInputDate(this.holiday.dateRange?.end)
    },
    isConfigured() {
      const k = this.holiday.rule.kind
      return k && k !== 'unset' && k !== 'agreement-required'
    },
    isCustom() {
      return typeof this.holiday.id === 'string' && this.holiday.id.startsWith('custom-')
    },
    summary() {
      return summarizeRule(this.holiday, {
        partyAName: this.partyAName,
        partyBName: this.partyBName,
      })
    },
    statusClass() {
      return this.isConfigured ? 'status-configured' : 'status-pending'
    },
    isFlagged() {
      return !!this.holiday.flaggedForDiscussion
    },
    canEdit() { return this.role === 'primary' },
    canComment() { return this.role === 'secondary' },
    // discussionNote is either null (empty) or an object of shape
    // { text, authorName, updatedAt }. Everything below normalizes reads.
    noteText() {
      var n = this.holiday.discussionNote
      return n && typeof n === 'object' ? (n.text || '') : ''
    },
    noteAuthor() {
      var n = this.holiday.discussionNote
      return n && typeof n === 'object' ? (n.authorName || '') : ''
    },
    noteUpdatedAt() {
      var n = this.holiday.discussionNote
      return n && typeof n === 'object' ? (n.updatedAt || null) : null
    },
    // The 💬 pip means "the other party left a discussion note" — it is based on
    // discussionNote, NOT whyThisMatters. An earlier duplicate of this computed
    // used whyThisMatters and was dead (the later key wins); it has been removed.
    // Do not reconcile the two: that changes what the pip tells a negotiating party.
    hasNote() {
      return this.noteText.trim().length > 0
    },
    noteLabel() {
      if (this.canComment) return 'Your note for discussion'
      if (this.noteAuthor) return 'Note from ' + this.noteAuthor
      return 'Note from the other party'
    },
    noteMeta() {
      if (!this.noteUpdatedAt) return ''
      try {
        var d = new Date(this.noteUpdatedAt)
        return 'Last edited ' + d.toLocaleString(undefined, {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit',
        })
      } catch (e) { return '' }
    },
  },
  // The details sub-panel used to stay open after the row was
  // collapsed, so reopening the row showed it already expanded.
  watch: {
    open(isOpen) {
      if (!isOpen) this.detailsOpen = false
    },
  },
  methods: {
    // Focus the header explicitly with preventScroll. Without it
    // the browser scrolls the newly focused element into view, fighting the
    // scroll anchoring in HolidayBuilder.toggleRow.
    onRowHeadClick(event) {
      const el = event && event.currentTarget
      if (el && el.focus) el.focus({ preventScroll: true })
      this.$emit('toggle', this.holiday.id)
    },
    onRuleChange(newRule) {
      this.emitPatch({ rule: newRule })
    },
    emitPatch(patch) {
      this.$emit('update', { id: this.holiday.id, patch })
    },
    yearRange(y) {
      return this.holiday.dateRanges?.[y] || { start: '', end: '' }
    },
    // What this year resolves to when no per-year entry is set (the
    // provincial default, or a legacy recurring dateRange if one exists).
    yearDefaultLabel(y) {
      const dr = resolveHolidayDate(this.holiday.id, y, {
        jurisdiction: this.jurisdiction,
        dateRange: this.holiday.dateRange,
      })
      if (!dr) return 'no dates'
      const fmt = (d) => d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
      return `${fmt(dr.start)} – ${fmt(dr.end)}`
    },
    // Set one school year's actual break dates. Clearing both fields drops
    // the entry so that year falls back to the default.
    patchYearDates(y, start, end) {
      const next = { ...(this.holiday.dateRanges || {}) }
      if (!start && !end) delete next[y]
      else next[y] = { start: start || end, end: end || start }
      this.emitPatch({ dateRanges: Object.keys(next).length ? next : null })
    },
    // Dates recur annually; the stored year is ignored by schedule-core. A
    // missing end mirrors the start (single-day holidays like birthdays).
    patchDates(start, end) {
      if (!start && !end) { this.emitPatch({ dateRange: null }); return }
      this.emitPatch({ dateRange: { start: start || end, end: end || start } })
    },
    // 'MM-DD' or 'YYYY-MM-DD' → a value the <input type="date"> accepts.
    _toInputDate(v) {
      if (!v) return ''
      const m = /^(?:(\d{4})-)?(\d{1,2})-(\d{1,2})$/.exec(String(v))
      if (!m) return ''
      const year = m[1] || String(new Date().getFullYear())
      return `${year}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
    },
    toggleFlag() {
      if (this.role === 'viewer') return
      this.emitPatch({ flaggedForDiscussion: !this.holiday.flaggedForDiscussion })
    },
    // Secondary-only: user typed in the note textarea. Empty text collapses
    // the field back to null so "has note" checks stay simple; non-empty
    // text is wrapped in the { text, authorName, updatedAt } object shape.
    // Writing a non-empty note automatically raises the discuss flag so
    // the primary sees the row in the toolbar counter + amber left border.
    // The flag is *not* auto-cleared when the note is emptied — the user
    // (or the primary via "Mark resolved") clears it explicitly.
    onNoteInput(newText) {
      if (!newText || !newText.trim()) {
        this.emitPatch({ discussionNote: null })
        return
      }
      this.emitPatch({
        discussionNote: {
          text: newText,
          authorName: this.currentUserName || '',
          updatedAt: Date.now(),
        },
        flaggedForDiscussion: true,
      })
    },
    // Primary-only: clear the note and the flag in one action.
    resolveDiscussion() {
      this.emitPatch({ discussionNote: null, flaggedForDiscussion: false })
    },
    // Wrap formatTimeOfDay so the template can reference it via `this.`.
    formatTimeOfDay,
  },
}
</script>

<style scoped>
.holiday-row {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.6rem 0.85rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.holiday-row.configured { border-color: #cbd5e1; }
.holiday-row.open {
  border-color: #3f6772;
  box-shadow: 0 1px 4px rgba(45,74,85,0.08);
  padding: 0.85rem 1rem;
}
/* Flagged rows get an amber 3px left-border so the "needs discussion"
   state is scannable at a glance, even in a long collapsed list. */
.holiday-row.flagged {
  border-left: 3px solid #f7a91b;
  padding-left: calc(0.85rem - 2px);
}
.holiday-row.flagged.open {
  padding-left: calc(1rem - 2px);
}
.holiday-row + .holiday-row { margin-top: 0.4rem; }

.row-head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  margin: -0.6rem -0.85rem;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  transition: background 0.15s;
  user-select: none;
}
.row-head:hover { background: #f1f5f9; }
.row-head:focus-visible {
  outline: 2px solid #3f6772;
  outline-offset: -2px;
}
.holiday-row.open .row-head {
  margin: -0.85rem -1rem 0.5rem;
  padding: 0.85rem 1rem;
}
.holiday-row.open .row-head:hover { background: transparent; }

.row-name {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  flex-shrink: 0;
  min-width: 0;
}
.row-summary {
  flex-grow: 1;
  font-size: 0.78rem;
  font-weight: 500;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.status-pending {
  color: #85581a;
  background: #fef6e0;
}
.status-configured {
  color: #244047;
  background: #d9e4e7;
}

.flag-prefix {
  color: #85581a;
  font-weight: 700;
  margin-right: 0.1rem;
}

.note-indicator {
  font-size: 0.85rem;
  flex-shrink: 0;
  cursor: default;
  line-height: 1;
}

.row-flag {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #94a3b8;
  font-family: inherit;
  font-size: 0.85rem;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.row-flag:hover {
  border-color: #f7a91b;
  color: #85581a;
  background: #fef6e0;
}
.row-flag.active {
  background: #f7a91b;
  color: #fff;
  border-color: #d8911a;
}
.row-flag.active:hover {
  background: #d8911a;
  border-color: #85581a;
}

.row-chevron {
  color: #64748b;
  font-size: 0.85rem;
  flex-shrink: 0;
  transition: transform 0.2s, color 0.15s;
  display: inline-block;
  width: 1em;
  text-align: center;
}
.row-head:hover .row-chevron { color: #34555f; }
.holiday-row.open .row-chevron {
  color: #3f6772;
  transform: rotate(180deg);
}

.row-remove {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #94a3b8;
  width: 24px;
  height: 24px;
  line-height: 1;
  border-radius: 50%;
  font-size: 0.95rem;
  cursor: pointer;
  font-family: inherit;
  flex-shrink: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.row-remove:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: #fee2e2;
}

/* Discussion note panel — appears at the top of the open row body.
   Writable when role='secondary', read-only when role='primary'.
   The primary sees this as an inbound comment from the other party;
   the secondary sees it as a scratchpad for what they want to raise. */
.discussion-note-panel {
  background: #fef6e0;
  border: 1px solid #fde9bf;
  border-left: 3px solid #f7a91b;
  border-radius: 8px;
  padding: 0.6rem 0.8rem;
  margin-bottom: 0.55rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.discussion-note-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
}
.discussion-note-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #85581a;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.discussion-note-textarea {
  background: #fff;
  border: 1px solid #f7a91b;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.4rem 0.55rem;
  font-size: 0.85rem;
  font-family: inherit;
  resize: vertical;
  min-height: 4.5rem;
}
.discussion-note-textarea:focus {
  outline: none;
  border-color: #d8911a;
  box-shadow: 0 0 0 2px rgba(247,169,27,0.2);
}
.discussion-note-readonly {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.4;
  color: #244047;
  white-space: pre-wrap;
  word-break: break-word;
}
.discussion-note-meta {
  margin: 0.15rem 0 0;
  font-size: 0.72rem;
  color: #85581a;
  font-style: italic;
  font-variant-numeric: tabular-nums;
}
.resolve-btn {
  background: #f7a91b;
  color: #fff;
  border: 1px solid #d8911a;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.3rem 0.7rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
}
.resolve-btn:hover {
  background: #d8911a;
  border-color: #85581a;
}

.details-toggle {
  margin-top: 0.5rem;
  background: transparent;
  border: 1px dashed #cbd5e1;
  color: #557d89;
  font-size: 0.78rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  align-self: flex-start;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.details-toggle:hover {
  border-color: #3f6772;
  border-style: solid;
  color: #34555f;
  background: #eef3f4;
}

/* "Use this arrangement for every row in this section" — propagate a
   configured row's rule onto its siblings. Sits below the rule editor
   when the row is open and the rule is set. */
.apply-to-all {
  margin-top: 0.65rem;
  background: transparent;
  border: 1px dashed #cbd5e1;
  color: #557d89;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 500;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  cursor: pointer;
  align-self: flex-start;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.apply-to-all:hover {
  border-color: #3f6772;
  border-style: solid;
  color: #34555f;
  background: #eef3f4;
}

.break-years {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.break-years-title {
  font-size: 0.74rem;
  font-weight: 600;
  color: #34555f;
}
.break-year-row {
  display: grid;
  grid-template-columns: 3.2em minmax(0, 1fr) auto minmax(0, 1fr) minmax(0, auto);
  gap: 0.45rem;
  align-items: center;
}
.break-year-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
}
.break-year-row input[type='date'] {
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.3rem 0.45rem;
  font-size: 0.8rem;
  font-family: inherit;
  min-width: 0;
}
.break-year-to { font-size: 0.74rem; color: #94a3b8; }
.break-year-default {
  font-size: 0.72rem;
  color: #94a3b8;
  font-style: italic;
  white-space: nowrap;
}
.break-year-clear {
  background: transparent;
  border: 1px solid #cbd5e1;
  color: #64748b;
  border-radius: 6px;
  width: 26px;
  height: 26px;
  line-height: 1;
  cursor: pointer;
  font-size: 0.9rem;
}
.break-year-clear:hover { color: #ef4444; border-color: #ef4444; background: #fee2e2; }

.dates-hint {
  margin: 0;
  font-size: 0.74rem;
  color: #b45309;
}
.dates-reset {
  background: transparent;
  border: 1px dashed #cbd5e1;
  color: #557d89;
  font-family: inherit;
  font-size: 0.74rem;
  font-weight: 500;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  justify-self: start;
}
.dates-reset:hover { border-color: #3f6772; border-style: solid; background: #eef3f4; color: #34555f; }

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 0.75rem;
  margin-top: 0.55rem;
  padding-top: 0.6rem;
  border-top: 1px dashed #e2e8f0;
}
.details-grid .span-2 { grid-column: span 2; }
.details-grid label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.78rem;
}
.details-grid label > span {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.7rem;
}
/* "Times to be agreed by the parties" checkbox row — flat layout above
   the pickup/dropoff inputs. When checked, the inputs grey out. */
.details-grid .times-agreed-row {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}
.details-grid .times-agreed-row > span {
  text-transform: none;
  letter-spacing: 0;
  color: #34555f;
  font-weight: 500;
  font-size: 0.82rem;
}
.details-grid .times-agreed-row input[type="checkbox"] {
  width: auto;
  margin: 0;
  cursor: pointer;
  padding: 0;
}
.details-grid label.disabled > span,
.details-grid label.disabled .time-hint {
  color: #cbd5e1;
}
.details-grid input[type="time"]:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}
.details-grid input,
.details-grid textarea {
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.4rem 0.55rem;
  font-size: 0.85rem;
  font-family: inherit;
  resize: vertical;
}
/* AM/PM rendering shown next to the native (locale-controlled) time input
   so the user always sees the friendly form regardless of OS time format. */
.time-hint {
  margin-top: 0.2rem;
  font-size: 0.72rem;
  color: #557d89;
  font-style: italic;
  font-variant-numeric: tabular-nums;
}
.row-flag:disabled { cursor: not-allowed; }
</style>
